import { API_BASE_URL } from "../../../config";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import RowActionsMenu from "../../Components/RowActionsMenu";
import CustomerLink from "../../Components/CustomerLink";

// Base API for customer service
const BASE_API = `${API_BASE_URL}/customer/api`;

const AgentManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [form, setForm] = useState({ agentName: "" , phone: "", latitude: "", longitude: "", operating: true });

  const getToken = useCallback(() => {
    const user = localStorage.getItem("user");
    if (!user) return null;
    try {
      return JSON.parse(user)?.token || null;
    } catch (_) {
      return null;
    }
  }, []);

  const authHeaders = useMemo(() => {
    const token = getToken();
    return token
      ? {
          accept: "*/*",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }
      : { accept: "*/*", "Content-Type": "application/json" };
  }, [getToken]);

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const resetForm = () => setForm({ agentName: "",  phone: "",latitude: "", longitude: "", operating: true });

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${BASE_API}/customers/by-category`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ type: "AGENT" }),
      });
      if (!resp.ok) {
        throw new Error(`Failed to load agents (${resp.status})`);
      }
      const data = await resp.json();
      // API may return different shapes; normalize
      let list = [];
      if (Array.isArray(data)) list = data;
      else if (Array.isArray(data?.agent)) list = data.agent;
      else if (Array.isArray(data?.data)) list = data.data;
      setAgents(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error("Agent fetch error", e);
      setError("ไม่สามารถดึงข้อมูลตัวแทนได้");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const createAgent = async (e) => {
    e?.preventDefault?.();
    setError("");
    try {
      const payload = { ...form };
      const resp = await fetch(`${BASE_API}/customers/agent`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!resp.ok) throw new Error("Create failed");
      await fetchAgents();
      resetForm();
      setCreating(false);
    } catch (e) {
      console.error("Create agent error", e);
      setError("ไม่สามารถสร้างตัวแทนได้");
    }
  };

  const startEdit = (agent) => {
    setEditingAgent(agent);
    setForm({
      agentName: agent.agentName ?? "",
        phone: agent.phone ?? "",
      latitude: String(agent.latitude ?? ""),
      longitude: String(agent.longitude ?? ""),
      operating: Boolean(agent.operating ?? true),
    });
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!editingAgent) return;
    const agentUid = editingAgent.agentCustomerUid;
    if (!agentUid) {
      setError("ไม่พบรหัสตัวแทน");
      return;
    }
    try {
      const body = {};
      ["agentName", "phone","latitude", "longitude", "operating"].forEach((k) => {
        if (form[k] !== undefined && form[k] !== null && form[k] !== "") body[k] = form[k];
      });
      const resp = await fetch(`${BASE_API}/customers/agent/${agentUid}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error("Update failed");
      await fetchAgents();
      setEditingAgent(null);
      resetForm();
    } catch (e) {
      console.error("Update agent error", e);
      setError("ไม่สามารถแก้ไขข้อมูลตัวแทนได้");
    }
  };

  const deleteAgent = async (agent) => {
    const agentUid = agent.agentCustomerUid;
    if (!agentUid) return;
    const confirmDelete = window.confirm(`ต้องการลบตัวแทน ${agent.agentName || "ไม่ทราบชื่อ"} หรือไม่?`);
    if (!confirmDelete) return;
    try {
      const resp = await fetch(`${BASE_API}/customers/agent/${agentUid}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!resp.ok) throw new Error("Delete failed");
      await fetchAgents();
    } catch (e) {
      console.error("Delete agent error", e);
      setError("ไม่สามารถลบตัวแทนได้");
    }
  };

  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="btc-container">
      <h1>หน้าต่างจัดการตัวแทน (Agent)</h1>

      {error && <p className="error">{error}</p>}

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {!creating && !editingAgent && (
          <button className="btc-register-button" onClick={() => setCreating(true)}>
            เพิ่มตัวแทน
          </button>
        )}
        {(creating || editingAgent) && (
          <button className="editButton" onClick={() => { setCreating(false); setEditingAgent(null); resetForm(); }}>
            ยกเลิก
          </button>
        )}
        <button className="editButton" onClick={fetchAgents}>รีเฟรช</button>
        <Link to="/customerData" className="editButton">ไปยังลูกค้าทั้งหมด</Link>
      </div>

      {(creating || editingAgent) && (
        <form onSubmit={editingAgent ? saveEdit : createAgent} style={{ marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, maxWidth: 600 }}>
            <label>
              ชื่อตัวแทน
              <input name="agentName" value={form.agentName} onChange={handleInput} required />
            </label>

              <label>
                  เบอร์โทร
                  <input name="phone" value={form.phone} onChange={handleInput} required />
              </label>

            <label>
              Latitude
              <input name="latitude" value={form.latitude} onChange={handleInput} />
            </label>
            <label>
              Longitude
              <input name="longitude" value={form.longitude} onChange={handleInput} />
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" name="operating" checked={!!form.operating} onChange={handleInput} />
              เปิดดำเนินการ
            </label>
          </div>
          <div style={{ marginTop: 12 }}>
            <button type="submit" className="btc-register-button">
              {editingAgent ? "บันทึกการแก้ไข" : "สร้างตัวแทน"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Loading agent data...</p>
      ) : (
        <table className="btc-table">
          <thead>
            <tr>
              <th>ชื่อตัวแทน</th>
                <th>เบอร์โทร</th>
              <th>Latitude</th>
              <th>Longitude</th>
              <th>สถานะ</th>
              <th>ตัวเลือก</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => {
              const agentUid = a.agentCustomerUid;
              return (
                <tr key={agentUid}>
                  <td><CustomerLink name={a.agentName || a.name} code={agentUid} type="AGENT" /></td>
                    <td>{a.phone || "-"}</td>
                  <td>{a.latitude ?? ""}</td>
                  <td>{a.longitude ?? ""}</td>
                  <td>{a.operating ? "Operating" : "Inactive"}</td>
                  <td>
                    <RowActionsMenu
                      actions={[
                        {
                          label: "แก้ไข",
                          icon: <i className="fas fa-edit"></i>,
                          onClick: () => startEdit(a),
                        },
                        {
                          label: "ลบ",
                          onClick: () => deleteAgent(a),
                          variant: "danger",
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AgentManagement;
